import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import RelayEnvironment from '../../../../RelayEnvironment';
import {isTicketingAvailable} from '../../util/ticketingAvailability';
import {hasPermission} from '../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../Permissions';
import Util from '../../util/util';
import MarkTicketNotificationReadMutation from '../../../../mutations/ticketing/notifications/markTicketNotificationRead';
import MarkAllTicketNotificationsReadMutation from '../../../../mutations/ticketing/notifications/markAllTicketNotificationsRead';

/** Synthetic native `publisherAction` values for ticket notifications. */
export const TICKET_NOTIFICATION_ACTION = {
	assigned: 'TICKET_ASSIGNED',
	mentioned: 'TICKET_MENTIONED',
	customer_replied: 'TICKET_CUSTOMER_REPLIED',
	sla_breached: 'TICKET_SLA_BREACHED',
} as const;

/** How many ticket notifications the merged bell fetches. */
export const TICKET_NOTIFICATIONS_PAGE_SIZE = 15;
/** Page size for the full "All notifications" page (vs the bell's smaller window). */
export const ALL_NOTIFICATIONS_PAGE_SIZE = 200;
/** Poll interval for ticket notifications while the menu is mounted (ms). */
export const TICKET_NOTIFICATIONS_POLL_MS = 60_000;

export interface TicketNotificationNode {
	id: string; // TicketNotification global id
	type: string; // assigned | mentioned | customer_replied | sla_breached
	read: boolean;
	createdAt: string | null; // ISO
	ticketId: string; // Ticket global id
	actorName?: string | null;
	ticketSubject?: string | null;
	watchedEvent?: string | null;
	actorPersonId?: string | null; // actor's Person global id; null when no actor
	slaType?: string | null; // response | resolution | null
}

/** The native-notification shape consumed by NotificationContainer/Notification.js. */
export interface AdaptedTicketNotification {
	id: string;
	countId: string;
	isTicketNotification: true;
	publisherPersonId: string | null; // ACC-538: actor's Person global id, resolved to avatar by populateNotificationData
	subscriberPersonId: null;
	publisherAction: string;
	entityType: 'TICKET';
	entityId: null;
	params: string; // JSON string: {ticketGlobalId}
	createdAt: string; // ms-epoch as string
	read: boolean;
}

export function adaptTicketNotification(node: TicketNotificationNode): AdaptedTicketNotification {
	const action = (TICKET_NOTIFICATION_ACTION as Record<string, string>)[node.type] ?? `TICKET_${node.type.toUpperCase()}`;
	const createdMs = node.createdAt ? String(Date.parse(node.createdAt)) : '0';
	return {
		id: node.id,
		countId: node.id,
		isTicketNotification: true,
		publisherPersonId: node.actorPersonId ?? null,
		subscriberPersonId: null,
		publisherAction: action,
		entityType: 'TICKET',
		entityId: null,
		params: JSON.stringify({
			ticketGlobalId: node.ticketId,
			actorName: node.actorName ?? null,
			ticketSubject: node.ticketSubject ?? null,
			watchedEvent: node.watchedEvent ?? null,
			slaType: node.slaType ?? null,
		}),
		createdAt: createdMs,
		read: node.read,
	};
}

/** True only when this user may query the TICKETS_VIEW-guarded ticketNotifications field. */
export function canSeeTicketNotifications(): boolean {
	try {
		return isTicketingAvailable() && hasPermission(PERMISSION_TYPE.TICKETS_VIEW);
	} catch {
		return false;
	}
}

const TicketNotificationsQuery = graphql`
	query ticketNotificationsQuery($first: Int, $unreadOnly: Boolean) {
		viewer {
			ticketNotifications(first: $first, unreadOnly: $unreadOnly) {
				unreadCount
				edges {
					node {
						id
						type
						read
						createdAt
						ticketId
						actorName
						ticketSubject
						watchedEvent
						actorPersonId
						slaType
					}
				}
			}
		}
	}
`;

interface FetchResult {
	notifications: AdaptedTicketNotification[];
	unreadCount: number;
}

/**
 * Imperatively fetch + adapt ticket notifications. Resolves to an empty result
 * (and issues no query) when the viewer lacks ticketing access, so the guarded
 * field is never queried for users who would get FORBIDDEN.
 */
export async function fetchTicketNotifications(first: number = TICKET_NOTIFICATIONS_PAGE_SIZE): Promise<FetchResult> {
	if (!canSeeTicketNotifications()) {
		return {notifications: [], unreadCount: 0};
	}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data: any = await fetchQuery(RelayEnvironment.getInstance(), TicketNotificationsQuery, {
		first,
		unreadOnly: false,
	}).toPromise();
	const conn = data?.viewer?.ticketNotifications;
	if (!conn) return {notifications: [], unreadCount: 0};
	return {
		notifications: conn.edges.map((e: {node: TicketNotificationNode}) => adaptTicketNotification(e.node)),
		unreadCount: conn.unreadCount ?? 0,
	};
}

export function commitMarkTicketNotificationRead(notificationId: string, onSuccess?: () => void): void {
	Util.CommitMutation(MarkTicketNotificationReadMutation, notificationId, onSuccess);
}

export function commitMarkAllTicketNotificationsRead(onSuccess?: () => void): void {
	Util.CommitMutation(MarkAllTicketNotificationsReadMutation, undefined, onSuccess);
}
